import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
    isAltaPayApplePay,
    isAltaPayKlarnaPayment,
} from '~/features/checkout/components/Payment/types';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { GIFT_CERTIFICATE } from '~/features/commerce-api/utils';
import { PaymentOption } from '~/lib';
import { useAltapaySession } from '~/services/altapay/hooks';
import { extractPaymentInstrumentByMethod } from '~/services/altapay/lib/utils';
import AltaPayPayment, { AltaPayPaymentType } from '~/services/altapay/payment';
import { ExtendedAltaPayPaymentMethod } from '~/services/altapay/types';
import { NetsPaymentType, PaymentMethod } from '~/shared/constants';
import { LockedFeatureKey, useLockedFeature } from '~/shared/hooks/useLockedFeature';
import { useFrame } from '~/shared/utils';
import { ascertainNumber } from '~/shared/utils/numbers';

const defaultPaymentOptions: PaymentOption[] = [
    {
        id: AltaPayPaymentType.APPLEPAY,
        label: 'Apple Pay',
        isEnabled: true,
    },
    {
        id: AltaPayPaymentType.MOBILEPAY,
        label: 'MobilePay',
        isEnabled: true,
    },
    {
        id: NetsPaymentType.CREDIT_CARD,
        label: '[Nets] Credit Card',
        isEnabled: true,
    },
    {
        id: NetsPaymentType.MOBILEPAY,
        label: '[Nets] With MobilePay',
        isEnabled: false,
    },
    {
        id: AltaPayPaymentType.CREDIT_CARD,
        label: '[AP] Credit Card',
        isEnabled: false,
    },
];

type AggregatedAltaPayOption = ExtendedAltaPayPaymentMethod & { _type: PaymentMethod.ALTAPAY };
type LoadingAltaPayOption = PaymentOption & { _type: PaymentMethod.ALTAPAY; isLoading: true };

export const isAltaPayOptionLoading = (
    option: AggregatedAltaPayOption | LoadingAltaPayOption,
): option is LoadingAltaPayOption => (option as LoadingAltaPayOption).isLoading === true;

type AggregatedPaymentOption =
    | AggregatedAltaPayOption
    | LoadingAltaPayOption
    | (PaymentOption & { _type: PaymentMethod.NETS });

interface AggregatedPaymentOptions {
    baseOptionsCount: number;
    options: AggregatedPaymentOption[];

    altaPaySessionId: string | undefined;
    altaPayErrors: unknown[];
    altaPayOptionsError: unknown;
    isLoadingAltaPay?: boolean;
    hasAltaPay: boolean;
    hasAltaPayOptionsOnly: boolean;
    hasNets: boolean;
}

const isAltaPayOption = (option: PaymentOption) =>
    Object.entries(AltaPayPaymentType).some(([_key, value]) => value === option.id);

const isNetsOption = (option: PaymentOption) =>
    option.id === NetsPaymentType.CREDIT_CARD || option.id === NetsPaymentType.MOBILEPAY;

export const useBasePaymentOptions = () => {
    const { data: frame } = useFrame();

    const configuredOptions = (frame?.settings?.paymentOptions || defaultPaymentOptions).filter(
        (option) => option.isEnabled,
    );

    const hasAltaPayOption = configuredOptions.some(isAltaPayOption);
    const hasNetsOption = configuredOptions.some(isNetsOption);

    return {
        options: configuredOptions,
        hasAltaPay: hasAltaPayOption,
        hasNets: hasNetsOption,
    };
};

export const usePaymentOptions = (config?: { enabled?: boolean }): AggregatedPaymentOptions => {
    const { enabled = true } = config || {};
    const { data: frame } = useFrame();
    const {
        basketId,
        paymentInstruments,
        isFreeOrder,
        c_remainingTotal: basketTotal = 0,
    } = useBasket();
    const { options: configuredOptions, hasAltaPay, hasNets } = useBasePaymentOptions();

    const {
        sessionId,
        error: altaPayInitError,
        getPaymentMethods,
    } = useAltapaySession({
        enabled: hasAltaPay && !isFreeOrder,
    });

    const {
        data: altaPayMethods,
        status: altaPayStatus,
        isLoading: isLoadingAltaPayOptions,
        error: altaPayOptionsError,
    } = useQuery({
        queryFn: getPaymentMethods,
        queryKey: ['PAYMENT_OPTIONS', basketId, sessionId],
        enabled: enabled && !!sessionId && !altaPayInitError,
    });

    const { isLocked: isApplePayLocked } = useLockedFeature(LockedFeatureKey.APPLE_PAY);

    const collateAltaPayOption = useCallback(
        (option: PaymentOption): ExtendedAltaPayPaymentMethod | undefined => {
            const paymentMethod = altaPayMethods?.find(
                (method) => AltaPayPayment.getPaymentType(method) === option.id,
            );

            if (paymentMethod) {
                return {
                    ...paymentMethod,
                    label: option.label ?? paymentMethod.name,
                    enabled: typeof option.isEnabled === 'boolean' ? option.isEnabled : true,
                };
            }

            return undefined;
        },
        [altaPayMethods],
    );

    const evaluatedOptions = useMemo(() => {
        const hasGiftCertificate = !!extractPaymentInstrumentByMethod(
            paymentInstruments,
            GIFT_CERTIFICATE,
        );

        const isTotalAboveKlarnaThreshold =
            basketTotal >=
            ascertainNumber(frame.settings?.klarnaMinimumAmountThreshold, {
                parser: parseFloat,
            });

        return configuredOptions.reduce((collection, option) => {
            if (isAltaPayOption(option)) {
                // We will wait for AltaPay to load and show the loading state.
                if (isLoadingAltaPayOptions) {
                    return collection.concat({
                        ...option,
                        _type: PaymentMethod.ALTAPAY,
                        isLoading: true,
                    });
                }

                // Once AltaPay has loaded, we will evaluate it against what we get from them.
                const evaluatedOption = collateAltaPayOption(option);

                if (evaluatedOption) {
                    /**
                     * We will not render the Apple Pay option if we have gift certificates
                     * on our basket to prevent further issues on Prod. We can re-enable it via
                     * a feature key for debugging.
                     *
                     * TODO(IMC-2285): Remove implementation once core issue has been resolved.
                     */
                    if (
                        isAltaPayApplePay(evaluatedOption) &&
                        hasGiftCertificate &&
                        isApplePayLocked
                    ) {
                        return collection;
                    }

                    /**
                     * We will not render the Klarna option if our order's total is not above
                     * the defined threshold in Umbraco.
                     */
                    if (isAltaPayKlarnaPayment(evaluatedOption) && !isTotalAboveKlarnaThreshold) {
                        return collection;
                    }

                    return collection.concat({
                        ...evaluatedOption,
                        _type: PaymentMethod.ALTAPAY,
                    });
                }
            } else if (isNetsOption(option)) {
                return collection.concat({
                    ...option,
                    _type: PaymentMethod.NETS,
                });
            }

            return collection;
        }, [] as AggregatedPaymentOption[]);
    }, [altaPayMethods, isLoadingAltaPayOptions]);

    /**
     * At the moment, we're only going to get errors from AltaPay and none from Nets.
     */
    const altaPayErrors = [altaPayInitError, altaPayOptionsError].filter((error) => !!error);

    const hasAltaPayOptionsOnly = useMemo(
        () => evaluatedOptions.every((option) => option._type === PaymentMethod.ALTAPAY),
        [evaluatedOptions],
    );

    return {
        options: evaluatedOptions,
        baseOptionsCount: configuredOptions.length || 0,
        altaPayErrors,
        altaPayOptionsError,
        isLoadingAltaPay: hasAltaPay && !altaPayErrors.length && altaPayStatus === 'pending',
        altaPaySessionId: sessionId,
        hasAltaPay,
        hasAltaPayOptionsOnly,
        hasNets,
    };
};
